import { Trans } from "react-i18next";import { useEffect, useState } from "react";

import { X } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle } from
"../../../components/ui/dialog";
import { getDetailedReport } from "../../../services/attendanceService";

import { formatDate, formatTime } from "../utils/dateUtils";

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="text-sm font-semibold per-file-hint">{label}</span>
      <span className="text-sm text-right text-foreground">{value ?? <span className="per-file-hint">—</span>}</span>
    </div>);

}

function Locality({ geoLocation }) {
  return geoLocation ?
  <span className="text-right">{geoLocation}</span> :

  <span className="per-file-hint">—</span>;

}

function Coordinates({ lat, lng }) {
  if (lat == null || lng == null) {
    return <span className="per-file-hint">—</span>;
  }

  const url = `https://www.google.com/maps/?q=${lat},${lng}`;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-block">

      <button
        className="px-3 py-1.5 rounded-md bg-primary text-white text-sm font-medium hover:opacity-90 transition">
        <Trans i18nKey="formActions.viewOnMap" />

      </button>
    </a>);

}

// Same as AttendanceDetailModal, but scoped to the timelog view: shows
// check-in/check-out + location/geo-location/map + intermediate entry/exit
// activity only. Deliberately excludes "Activities Completed" and
// "Tomorrow's Plan" (those belong to the separate viewReport / tomorrowPlan
// actions).
export function TimeLogModal({ record, onOpenChange }) {
  const [intermediateEvents, setIntermediateEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!record?.attendanceId) {
      setIntermediateEvents([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    getDetailedReport(record.attendanceId)
    .then((data) => {
      const events = Array.isArray(data) ? data : [];
      const filtered = events
      .filter((e) => e.intermediateAction === "Entry" || e.intermediateAction === "Exit")
      .sort((a, b) => new Date(a.actionTimestamp) - new Date(b.actionTimestamp));
      setIntermediateEvents(filtered);
    })
    .catch((err) => setError(err.message))
    .finally(() => setLoading(false));
  }, [record]);

  return (
    <Dialog open={!!record} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="bg-[#0a1230]/50 backdrop-blur-xs" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border border-white/60 bg-white shadow-[0_30px_80px_-20px_rgba(30,51,131,0.45)] font-sans">
          {record &&
          <>
              <div className="flex items-center justify-between px-6 py-4 border-b border-(--color-text-border)/70">
                <div>
                  <DialogTitle className="text-xl font-bold text-foreground"><Trans i18nKey="attendance.viewTimeLog" /></DialogTitle>
                  <p className="text-sm per-file-hint mt-0.5">
                    {record.empName || "—"} <span className="text-(--color-text-border)">·</span> <Trans i18nKey="common.id" /> {record.empId ?? "—"}
                  </p>
                  <p className="text-sm per-file-hint">{formatDate(record.attendanceDate)}</p>
                </div>
                <DialogPrimitive.Close className="h-9 w-9 grid place-items-center rounded-xl per-file-hint hover:bg-workspace/60 hover:text-primary transition cursor-pointer">
                  <X className="h-5 w-5" />
                  <span className="sr-only"><Trans i18nKey="formActions.close" /></span>
                </DialogPrimitive.Close>
              </div>

              <div className="px-6 py-2">
                <div className="divide-y divide-(--color-text-border)/40">
                  <Row label="Check In" value={<span className="font-mono">{formatTime(record.checkInTime)}</span>} />
                  <Row label="Location" value={record.checkInLocation} />
                  <Row label="Geo Location" value={<Locality geoLocation={record.checkInGeoLocation} />} />
                  <Row label="Coordinate" value={<Coordinates lat={record.checkInLatitude} lng={record.checkInLongitude} />} />
                </div>

                {loading && <p className="py-3 text-sm per-file-hint"><Trans i18nKey="attendance.loadingIntermediateActivity" /></p>}
                {error && <p className="py-3 text-sm text-error">{error}</p>}

                {!loading && intermediateEvents.length > 0 &&
              <div className="mt-2">
                    <p className="text-sm font-semibold text-foreground pt-3 pb-1"><Trans i18nKey="attendance.intermediateActivity" /></p>
                    <div className="divide-y divide-(--color-text-border)/40">
                      {intermediateEvents.map((event, i) =>
                  <div key={i} className="py-2.5 space-y-1">
                          <p
                      className={`text-xs font-semibold uppercase tracking-wide ${
                      event.intermediateAction === "Exit" ? "text-error" : "text-primary"}`
                      }>

                            {event.intermediateAction === "Exit" ? "Out" : "In"}
                          </p>
                          <Row label="Time" value={<span className="font-mono">{formatTime(event.actionTimestamp)}</span>} />
                          {event.reason ? <Row label="Reason" value={event.reason} /> : null}
                        </div>
                  )}
                    </div>
                  </div>
              }

                <div className="divide-y divide-(--color-text-border)/40 mt-2">
                  <Row label="Check Out" value={record.checkOutTime ? <span className="font-mono">{formatTime(record.checkOutTime)}</span> : undefined} />
                  <Row label="Location" value={record.checkOutLocation} />
                  <Row label="Geo Location" value={<Locality geoLocation={record.checkOutGeoLocation} />} />
                  <Row label="Coordinate" value={<Coordinates lat={record.checkOutLatitude} lng={record.checkOutLongitude} />} />
                </div>
              </div>

              <div className="h-4" />
            </>
          }
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>);

}